import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

export interface ThemeState {
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'light',
      setTheme: (theme) => set({ theme }),
    }),
    { name: 'cardston-theme' }
  )
);

export interface UserProfile {
  uid: string;
  email: string | null;
  role: 'admin' | 'vendor' | 'user';
  isAdmin: boolean;
  displayName?: string;
  storeName?: string;
  storeDescription?: string;
  policies?: string;
  contactEmail?: string;
  socialLinks?: Record<string, string>;
  pickupLocation?: string;
  pickupAvailability?: string;
  pickupDays?: number[];
  pickupStartTime?: string;
  pickupEndTime?: string;
  pickupSchedules?: Record<string, { startTime: string; endTime: string; enabled: boolean }>;
  city?: string;
  stripeAccountId?: string;
  stripeOnboarded?: boolean;
  bannerImage?: string;
  bannerColors?: string[];
  bannerType?: 'image' | 'color' | 'gradient';
  gradientType?: string;
  profilePicture?: string;
  themeColors?: string[];
  themeType?: 'solid' | 'gradient';
}

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  isAuthReady: boolean;
  setUser: (user: User | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  setAuthReady: (ready: boolean) => void;
  fetchProfile: (uid: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  profile: null,
  isAuthReady: false,
  setUser: (user) => set({ user }),
  setProfile: (profile) => set({ profile }),
  setAuthReady: (ready) => set({ isAuthReady: ready }),
  fetchProfile: async (uid: string) => {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        set({ profile: { ...data, uid: docSnap.id } as UserProfile });
      } else {
        set({ profile: null });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      set({ profile: null });
    }
  },
}));

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  description?: string;
  vendorId?: string;
  vendorName?: string;
  stock?: number;
  maxOrderQuantity?: number;
}

interface CartState {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      total: 0,
  addItem: (item) => {
    const { items } = get();
    const existingItem = items.find((i) => i.id === item.id);
    let newItems;
    if (existingItem) {
      const newQuantity = existingItem.quantity + item.quantity;
      if (existingItem.stock !== undefined) {
        if (newQuantity > existingItem.stock) {
          return;
        }
      } else {
        const fallbackMax = existingItem.maxOrderQuantity !== undefined ? existingItem.maxOrderQuantity : 5;
        if (newQuantity > fallbackMax) {
          return;
        }
      }
      newItems = items.map((i) =>
        i.id === item.id ? { ...i, quantity: newQuantity } : i
      );
    } else {
      if (item.stock !== undefined) {
        if (item.quantity > item.stock) {
          return;
        }
      } else {
        const fallbackMax = item.maxOrderQuantity !== undefined ? item.maxOrderQuantity : 5;
        if (item.quantity > fallbackMax) {
          return;
        }
      }
      newItems = [...items, item];
    }
    set({ 
      items: newItems,
      total: newItems.reduce((sum, i) => sum + i.price * i.quantity, 0)
    });
  },
  removeItem: (id) => {
    const newItems = get().items.filter((i) => i.id !== id);
    set({ 
      items: newItems,
      total: newItems.reduce((sum, i) => sum + i.price * i.quantity, 0)
    });
  },
  updateQuantity: (id, quantity) => {
    const { items } = get();
    const item = items.find((i) => i.id === id);
    if (!item) return;
    
    if (item.stock !== undefined) {
      if (quantity > item.stock) {
        quantity = item.stock;
      }
    } else {
      const fallbackMax = item.maxOrderQuantity !== undefined ? item.maxOrderQuantity : 5;
      if (quantity > fallbackMax) {
        quantity = fallbackMax;
      }
    }
    
    const newItems = items.map((i) => (i.id === id ? { ...i, quantity } : i));
    set({
      items: newItems,
      total: newItems.reduce((sum, i) => sum + i.price * i.quantity, 0)
    });
  },
  clearCart: () => set({ items: [], total: 0 }),
    }),
    { name: 'cardston-cart' }
  )
);
